import axios from '@/axiosInterceptor';
import churnzeroService from '@/services/churnzero';
import { setGrafanaFaroUser, resetGrafanaFaroUser } from '@/services/grafana';

import { showGTM } from '@/common/gtmHelpers';

const state = () => ({
  currentUser: null,
  users: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  watchdog: {
    isActive: false,
    canViewShowPage: true,
    canCarrierSearch: true,
    ui: false,
  },
  intercomIsBooted: false,
  intercomIsUserLoggedIn: false,
  activeTrialBannerActive: false,
  featureFlags: {},
  scopedUsers: {},
  scopedUsersPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  hasScopedUsersLoaded: false,
  isSearchUsersLoading: false,
  inquiries: null,
});

// getters
const getters = {
  canSeeSafetyData(state) {
    const blockSafetyDataScope =
      state.currentUser?.scopes?.includes('block_safety_data');
    if (blockSafetyDataScope) {
      return true;
    } else {
      return false;
    }
  },
  getFeatureFlag: (state) => (key) => {
    const feature = state.featureFlags[key];
    return feature ? feature : null;
  },
  brokerUserWithOnboardingAccess: (state) => {
    return (
      state.currentUser?.company?.company_type === 'broker' &&
      state.currentUser?.company?.carrier_id > 0 &&
      state.currentUser?.scopes.includes('manage_users')
    );
  },
  isInquiryApproved: (state) => {
    return state.inquiries?.[0]?.status === 'approved';
  },
  getLatestInquiry: (state) => {
    return state.inquiries?.length > 0 ? state.inquiries[0] : state.inquiries;
  },
  inquiryStatus: (state) => {
    if (state.currentUser?.last_verified_identity_at) {
      return 'approved';
    }

    return state.inquiries?.length > 0
      ? state.inquiries[0].status
      : state.inquiries;
  },
  hideMyCarriersTab: (state) => {
    const isHideCarriersFeatureEnabled =
      state.featureFlags?.hide_my_carriers_tab_for_no_connect_plan?.enabled;
    const hasConnectAccess = state.currentUser?.paywall?.can_connect;
    return isHideCarriersFeatureEnabled && !hasConnectAccess;
  },
  canConnect: (state) => {
    const companyType = state.currentUser?.company?.company_type;
    const allowedTypes = ['broker', 'insurance_producer', 'insurance_insurer'];
    return (
      allowedTypes.includes(companyType) &&
      !!state.currentUser?.paywall?.can_connect
    );
  },
  canRule: (state) => {
    const companyType = state.currentUser?.company?.company_type;
    const allowedTypes = ['broker', 'insurance_producer', 'insurance_insurer'];
    return (
      allowedTypes.includes(companyType) &&
      !!state.currentUser?.paywall?.can_rule
    );
  },
  isInsuranceCompanyType: (state) => {
    return (
      state.currentUser?.company?.company_type?.includes('insurance_') || false
    );
  },
  isAnalyticsPageEnabled: (state, getters) => {
    const isInsuranceCompany =
      getters.isInsuranceCompanyType &&
      state.featureFlags?.display_metabase_dashboard?.enabled;
    return (
      isInsuranceCompany ||
      (state.currentUser?.scopes?.includes('compliance') &&
        state.currentUser?.paywall?.can_connect)
    );
  },
};

// actions
const actions = {
  // Refresh the state of the current user; check
  // first if in state and if not then go get
  // from the server
  async refreshCurrentUser({ dispatch, state }) {
    if (state.currentUser?.id > 0) {
      return state.currentUser;
    } else {
      return await dispatch('serverRefreshCurrentUser');
    }
  },
  async refreshCurrentInquiries({ dispatch, state }) {
    if (state.inquiries?.length > 0 || state.inquiries === false) {
      return state.inquiries;
    } else {
      return await dispatch('getUserInquiries', state.currentUser?.id);
    }
  },

  // Refresh the user from the server using the identify
  // endpoint
  async serverRefreshCurrentUser({ dispatch, commit, state }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/refresh_jwt');
      commit('setCurrentUser', res.data);
      dispatch('setGTMVars', res.data);

      const isWatchdog = res?.data?.paywall?.is_watchdog;
      const canViewShowPageValue =
        res?.data?.watchdog_limits?.can_view_full_carrier_profile_for_watchdog;
      const canCarrierSearchValue =
        res?.data?.watchdog_limits?.can_carrier_search_for_watchdog;

      if (isWatchdog) {
        if (state.watchdog.isActive !== isWatchdog) {
          commit('setWatchdogActive', isWatchdog);
        }
        if (state.watchdog.canViewShowPage !== canViewShowPageValue) {
          commit('setWatchdogCanViewShowPage', canViewShowPageValue);
        }
        if (state.watchdog.canCarrierSearch !== canCarrierSearchValue) {
          commit('setWatchdogCanCarrierSearch', canCarrierSearchValue);
        }
      }

      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e.response?.status)) {
        commit('setCurrentUser', null);
        return e.response;
      } else if (axios.isAxiosError(e) && [422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // Refresh the JWT cookie after the user
  // has signed up
  async serverRefreshJwt({ dispatch, commit }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/refresh_jwt');
      commit('setCurrentUser', res.data);
      dispatch('setGTMVars', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e.response?.status)) {
        commit('setCurrentUser', null);
        return e.response;
      } else {
        throw e;
      }
    }
  },

  setGTMVars(_, user) {
    if (showGTM()) {
      /* eslint-disable-next-line */
      dataLayer.push({
        user_id: user?.id || null,
        company_id: user?.company?.id || null,
        company_name: user?.company?.legal_name || null,
        company_type: user?.company?.company_type || null,
        company_plan_id: user?.company?.plan_id || null,
        company_status: user?.company?.status || null,
        requested_carrier_id: user?.requested_carrier_id || null,
        carrier_id: user?.company?.carrier_id || null,
        user_can_api: user?.paywall?.can_api || null,
        user_can_api_with_insights:
          user?.paywall?.can_api_with_insights || null,
        user_can_connect: user?.paywall?.can_connect || null,
        user_can_export: user?.paywall?.can_export || null,
        user_can_export_with_insights:
          user?.paywall?.can_export_with_insights || null,
        user_can_view_insights: user?.paywall?.can_view_insights || null,
        user_can_view_insurance_certificates:
          user?.paywall?.can_view_insurance_certificates || null,
        user_scope_manage_users: user?.scopes?.includes('manage_users') || null,
        user_scope_sourcing: user?.scopes?.includes('sourcing') || null,
        user_scope_manage_rule_overrides:
          user?.scopes?.includes('manage_rule_overrides') || null,
        user_scope_onboarding: user?.scopes?.includes('onboarding') || null,
        user_scope_surveys_publish:
          user?.scopes?.includes('surveys_publish') || null,
        user_scope_surveys_workflow:
          user?.scopes?.includes('surveys_workflow') || null,
        user_scope_insurance_workflow:
          user?.scopes?.includes('insurance_workflow') || null,
        user_scope_support_workflow:
          user?.scopes?.includes('support_workflow') || null,
        user_scope_view_bank_accounts:
          user?.scopes?.includes('view_bank_accounts') || null,
        user_scope_connection_notifications:
          user?.scopes?.includes('connection_notifications') || null,
      });
    }
  },
  // Create a verification for logging in
  async postSignIn(_, body) {
    try {
      const res = await axios.post(process.env.API_IDENTITY + '/sign_in', body);
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 409, 422, 429].includes(e.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async getLogout({ commit }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/sign_out');
      if ([204].includes(res?.status)) {
        // Clear Grafana Faro user session
        resetGrafanaFaroUser();

        let noUser = {
          id: null,
          first_name: null,
          last_name: null,
          company: {
            id: null,
            status: null,
          },
        };
        commit('setCurrentUser', noUser);
        !!localStorage && localStorage.clear();
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  // Send the sign in code
  async postSignInCode({ state, commit }, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY + '/sign_in/code',
        body
      );
      commit('setCurrentUser', res.data);

      const isWatchdog = res?.data?.paywall?.is_watchdog;
      const canViewShowPageValue =
        res?.data?.watchdog_limits?.can_view_full_carrier_profile_for_watchdog;
      const canCarrierSearchValue =
        res?.data?.watchdog_limits?.can_carrier_search_for_watchdog;

      if (isWatchdog) {
        if (state.watchdog.isActive !== isWatchdog) {
          commit('setWatchdogActive', isWatchdog);
        }
        if (state.watchdog.canViewShowPage !== canViewShowPageValue) {
          commit('setWatchdogCanViewShowPage', canViewShowPageValue);
        }
        if (state.watchdog.canCarrierSearch !== canCarrierSearchValue) {
          commit('setWatchdogCanCarrierSearch', canCarrierSearchValue);
        }
      }

      if (showGTM()) {
        /* eslint-disable-next-line */
        dataLayer.push({
          event: 'login',
          target: 'Button',
          action: 'click',
          'target-properties': 'User Login',
          value: res?.data?.id,
        });
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  // Create a verification for signing up
  async postSignUp(_, body) {
    try {
      const res = await axios.post(process.env.API_IDENTITY + '/sign_up', body);

      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 409, 422].includes(e.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async postSignUpCode({ commit }, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY + '/sign_up/code',
        body
      );
      commit('setCurrentUser', res.data);
      if (showGTM()) {
        /* eslint-disable-next-line */
        dataLayer.push({
          event: 'login',
          target: 'Button',
          action: 'click',
          'target-properties': 'User Login',
          value: res?.data?.id,
        });
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchSignUpDetails({ commit }, body) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY + '/sign_up/details',
        body
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async postCreateCompany(_, body) {
    try {
      return await axios.post(
        process.env.API_IDENTITY + '/sign_up/create_company',
        body
      );
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchCheckOpenInvitation() {
    try {
      return await axios.patch(
        process.env.API_IDENTITY + '/sign_up/check_for_open_user_invitation'
      );
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 404, 422].includes(e.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchClaimOpenInvitation({ commit }, body) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY + '/sign_up/claim_open_user_invitation',
        body
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async updateFirstOrLastName({ commit }, fields) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY + '/sign_up/details',
        { first_name: fields.firstName, last_name: fields.lastName }
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getUsers({ commit }, currentPage) {
    try {
      const res = await axios.get(
        `${process.env.API_IDENTITY}/api/v1/users?page=${currentPage}&per_page=50&q[s]=first_name asc`
      );
      commit('setUsers', res.data);
      commit('setPagination', res.headers);
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // stashing
  async getScopedUsers({ commit }, options = {}) {
    const { page = 1, scope = 'manage_tickets' } = options;
    try {
      const res = await axios.get(
        `${process.env.API_IDENTITY}/api/v1/users?page=${page}&per_page=50&q[s]=first_name asc&q[user_scopes_scope_eq]=${scope}`
      );
      commit('setHasScopedUsersLoaded', true);
      commit('setScopedUsers', { scope: scope, users: res.data });
      commit('setScopedUsersPagination', res.headers);
    } catch (e) {
      commit('setHasScopedUsersLoaded', true);
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // eslint-disable-next-line no-unused-vars
  async deleteUser({ commit }, id) {
    try {
      const res = await axios.delete(
        `${process.env.API_IDENTITY}/api/v1/users/${id}`
      );
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // eslint-disable-next-line no-unused-vars
  async setScopes({ commit }, data) {
    try {
      const res = await axios.patch(
        `${process.env.API_IDENTITY}/api/v1/users/${data.id}`,
        data.scopes
      );
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async sendIntercomEvent({ state }, { event_name, metadata }) {
    if (!state.intercomIsBooted || !window?.Intercom) {
      console.log(
        'Bypassing ' + event_name + ' event send, Intercom not booted'
      );
      return;
    }
    try {
      await window.Intercom('trackEvent', event_name, metadata);
    } catch {
      console.log('Error submitting Intercom event - ' + event_name);
    }
  },
  async getSearchUsers({ commit }, { searchTerm, currentPage }) {
    commit('setLoadingUsers', true);
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/search_company_users?search_term=${searchTerm}&page=${currentPage}`
      );
      commit('setUsers', res.data);
      commit('setPagination', res.headers);
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setLoadingUsers', false);
    }
  },
  async getOutlookKey({ state }) {
    try {
      return await axios.get(
        `${process.env.API_IDENTITY}/api/v1/users/${state.currentUser?.id}/highway_for_outlook_api_key`
      );
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async refreshCurrentFeatureFlags({ dispatch, state }, user) {
    if (Object.keys(state.featureFlags)?.length > 0) {
      return state.featureFlags;
    } else {
      return await dispatch('getFeatureFlags', user);
    }
  },
  async getFeatureFlags({ commit }, user) {
    if (!user?.id) return;

    try {
      const requests = [
        axios.get(
          `${process.env.API_IDENTITY}/api/flipper/actors/User;${user.id}`
        ),
      ];

      if (user?.company?.id) {
        requests.push(
          axios.get(
            `${process.env.API_IDENTITY}/api/flipper/actors/Company;${user.company.id}`
          )
        );
      }

      const [userRes, companyRes] = await Promise.all(requests);
      const userFlags = userRes.data.features || {};
      const companyFlags = companyRes?.data?.features || {};

      const mergedFlags = {};

      const allKeys = new Set([
        ...Object.keys(userFlags),
        ...Object.keys(companyFlags),
      ]);

      for (const key of allKeys) {
        const userVal = userFlags[key]?.enabled ?? false;
        const companyVal = companyFlags[key]?.enabled ?? false;
        mergedFlags[key] = { enabled: companyVal || userVal };
      }

      commit('setFeatureFlags', mergedFlags);
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getUserInquiries({ commit, state }, userId) {
    if (!userId) return;
    if (state.currentUser?.last_verified_identity_at) {
      return;
    }
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries?q[s]=id desc&q[user_id_eq]=${userId}&q[is_type_eq]=idv`
      );
      if (!res?.data || res.data?.length < 1) {
        commit('setInquiries', false);
      } else {
        commit('setInquiries', res.data);
      }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 401].includes(e?.response?.status)
      ) {
        console.warn('Error getting user inquiries', e?.response);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async pollInquiries({ commit }, userId) {
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries?q[s]=id desc&q[user_id_eq]=${userId}`
      );
      if (!res?.data || res.data?.length < 1) {
        commit('setInquiries', false);
      } else {
        commit('setInquiries', res.data);
      }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 401].includes(e?.response?.status)
      ) {
        console.warn('Error getting user inquiries', e?.response);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getInquiry({ state }) {
    const userId = state?.currentUser?.id;
    if (state?.currentUser?.last_verified_identity_at) {
      console.log('User has been verified, bypassing getInquiry');
      return;
    }
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries/get_inquiry`
      );
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
};

// mutations
const mutations = {
  setCurrentUser(state, user) {
    state.currentUser = user;

    // Identify user in ChurnZero when user is set
    if (user && user.id && user.company?.id) {
      churnzeroService.identifyUser(
        user.company.id.toString(),
        user.id.toString(),
        user
      );
    }

    // Set user session in Grafana Faro for tracking
    if (user && user.id) {
      setGrafanaFaroUser(user);
    }
  },
  setUsers(state, users) {
    state.users = users;
  },
  setScopedUsers(state, { scope, users }) {
    state.scopedUsers = { ...state.scopedUsers, [scope]: users };
  },
  setScopedUsersPagination(state, headers) {
    state.scopedUsersPagination = {
      currentPage: headers.currentpage,
      totalPages: headers.totalpages,
      totalRecords: headers.totalitems,
    };
  },
  setHasScopedUsersLoaded(state, bool) {
    state.hasScopedUsersLoaded = bool;
  },
  setPagination(state, headers) {
    state.pagination = {
      currentPage: headers.currentpage,
      totalPages: headers.totalpages,
      totalRecords: headers.totalitems,
    };
  },
  setWatchdogActive(state, bool) {
    state.watchdog.isActive = bool;
  },
  setWatchdogCanViewShowPage(state, bool) {
    state.watchdog.canViewShowPage = bool;
  },
  setWatchdogCanCarrierSearch(state, bool) {
    state.watchdog.canCarrierSearch = bool;
  },
  setWatchdogInterface(state, bool) {
    state.watchdog.ui = bool;
  },
  setIntercomIsBooted(state, bool) {
    state.intercomIsBooted = bool;
  },
  setIntercomIsUserLoggedIn(state, bool) {
    state.intercomIsUserLoggedIn = bool;
  },
  setActiveTrialBanner(state, bool) {
    state.activeTrialBannerActive = bool;
  },
  setFeatureFlags(state, features) {
    state.featureFlags = features;
  },
  setLoadingUsers(state, bool) {
    state.isSearchUsersLoading = bool;
  },
  setInquiries(state, inquiries) {
    state.inquiries = inquiries;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
